/**
 * Tabela com colunas ajustáveis (item 29), para as tabelas de lista do sistema:
 * - arrastar a borda direita do título muda a largura da coluna (duplo clique volta à largura automática);
 * - o botão de colunas (canto direito do título) esconde e mostra colunas, inclusive as extras: um <th data-col="extra">
 *   começa escondido e entra quando a pessoa marca (ex.: os campos do projeto na lista de Projetos);
 * - fica guardado neste navegador, por tabela (id), e "Restaurar padrão" volta ao original.
 * As colunas são reconhecidas pelo título; esconder usa CSS (nth-child), e linhas que ocupam a tabela inteira (colspan)
 * nunca somem.
 */
import { useEffect, useLayoutEffect, useRef, useState, type TableHTMLAttributes } from 'react';
import clsx from 'clsx';

interface Prefs { hidden: string[]; shown: string[]; widths: Record<string, number> }
interface Head { label: string; extra: boolean; fixed: boolean }

const KEY = (id: string) => `pmis.cols.${id}`;
const load = (id: string): Prefs => {
  try { return { hidden: [], shown: [], widths: {}, ...(JSON.parse(localStorage.getItem(KEY(id)) ?? '{}') as Partial<Prefs>) }; } catch { return { hidden: [], shown: [], widths: {} }; }
};
const save = (id: string, p: Prefs) => { try { localStorage.setItem(KEY(id), JSON.stringify(p)); } catch { /* sem armazenamento: vale só nesta tela */ } };
/** Título da coluna como a pessoa vê (sem as setas de ordenação). */
const labelOf = (th: Element, i: number) => (th.getAttribute('data-col-label') ?? th.textContent ?? '').replace(/[▲▼]/g, '').trim() || `#${i + 1}`;

export function ColumnsTable({ id, className, children, ...rest }: TableHTMLAttributes<HTMLTableElement> & { id: string }) {
  const wrap = useRef<HTMLDivElement>(null);
  const table = useRef<HTMLTableElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [heads, setHeads] = useState<Head[]>([]);
  const [prefs, setPrefsState] = useState<Prefs>(() => load(id));
  const [edges, setEdges] = useState<{ label: string; x: number; h: number }[]>([]);
  const [open, setOpen] = useState<{ top: number; right: number } | null>(null);
  const scope = `ct-${id.replace(/[^\w-]/g, '-')}`;
  const setPrefs = (p: Prefs) => { setPrefsState(p); save(id, p); };

  const isHidden = (h: Head) => !h.fixed && (prefs.hidden.includes(h.label) || (h.extra && !prefs.shown.includes(h.label)));

  // Os títulos podem mudar a cada render (colunas condicionais, como as de valores): lê de novo e só atualiza se mudou.
  useLayoutEffect(() => {
    const ths = table.current ? [...table.current.querySelectorAll(':scope > thead > tr:first-child > th')] : [];
    const next = ths.map((th, i) => ({ label: labelOf(th, i), extra: th.getAttribute('data-col') === 'extra', fixed: th.getAttribute('data-col') === 'fixed' || !(th.textContent ?? '').trim() }));
    if (JSON.stringify(next) !== JSON.stringify(heads)) setHeads(next);
  });

  // Posição das bordas (para arrastar), medida depois do layout e a cada mudança de tamanho.
  useLayoutEffect(() => {
    const t = table.current;
    if (!t) return;
    const measure = () => {
      const ths = [...t.querySelectorAll<HTMLTableCellElement>(':scope > thead > tr:first-child > th')];
      setEdges(ths.map((th, i) => ({ label: labelOf(th, i), x: t.offsetLeft + th.offsetLeft + th.offsetWidth, h: th.offsetHeight, visible: th.offsetWidth > 0 }))
        .filter((e) => e.visible).map(({ label, x, h }) => ({ label, x, h })));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(t);
    return () => ro.disconnect();
  }, [heads, prefs]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!menu.current?.contains(e.target as Node)) setOpen(null); };
    const scroll = () => setOpen(null);
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', scroll);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('resize', scroll); };
  }, [open]);

  const css = heads.map((h, i) => {
    const cells = `[data-ct="${scope}"] > table > * > tr > :nth-child(${i + 1}):not([colspan])`;
    const w = prefs.widths[h.label];
    return `${isHidden(h) ? `${cells}{display:none}` : ''}${w ? `${cells}{width:${w}px;min-width:${w}px;max-width:${w}px;overflow:hidden;text-overflow:ellipsis}` : ''}`;
  }).join('') + `[data-ct="${scope}"] > table > thead > tr > th:last-child{padding-right:2.25rem}`;

  const startDrag = (label: string, e: React.PointerEvent) => {
    e.preventDefault();
    const th = [...(table.current?.querySelectorAll<HTMLTableCellElement>(':scope > thead > tr:first-child > th') ?? [])].find((x, i) => labelOf(x, i) === label);
    if (!th) return;
    const x0 = e.clientX;
    const w0 = th.offsetWidth;
    let last = prefs;
    const move = (ev: PointerEvent) => { last = { ...prefs, widths: { ...prefs.widths, [label]: Math.max(48, Math.round(w0 + ev.clientX - x0)) } }; setPrefsState(last); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); save(id, last); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const resetWidth = (label: string) => { const { [label]: _, ...widths } = prefs.widths; setPrefs({ ...prefs, widths }); };
  const toggle = (h: Head) => {
    const hide = !isHidden(h);
    const visible = heads.filter((x) => !x.fixed && !isHidden(x));
    if (hide && visible.length <= 1) return;
    setPrefs(h.extra
      ? { ...prefs, shown: hide ? prefs.shown.filter((l) => l !== h.label) : [...prefs.shown, h.label], hidden: prefs.hidden.filter((l) => l !== h.label) }
      : { ...prefs, hidden: hide ? [...prefs.hidden, h.label] : prefs.hidden.filter((l) => l !== h.label) });
  };
  const choosable = heads.filter((h) => !h.fixed);
  const changed = prefs.hidden.length || prefs.shown.length || Object.keys(prefs.widths).length;

  return (
    <div ref={wrap} data-ct={scope} className="relative w-max min-w-full">
      {/* Botão de colunas preso no canto direito da parte visível (a tabela pode rolar para os lados e para baixo). */}
      {choosable.length > 1 ? (
        <div className="no-print sticky top-0 z-[3] h-0">
          <div ref={menu} className="sticky right-1 ml-auto w-fit pr-1 pt-1">
            <button type="button" aria-expanded={!!open}
              onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setOpen(open ? null : { top: r.bottom + 4, right: window.innerWidth - r.right }); }} aria-label="Escolher as colunas" title="Colunas: esconder, mostrar e largura"
              className={clsx('grid size-7 place-items-center rounded-md border border-line bg-panel text-muted shadow-sm hover:text-fg', changed && 'text-accent')}>
              <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden><rect x="3.5" y="4.5" width="17" height="15" rx="2" /><path d="M9.5 4.5v15M14.5 4.5v15" /></svg>
            </button>
            {open ? (
              <div className="fixed z-50 max-h-[60vh] w-64 overflow-auto rounded-xl border border-line bg-panel p-2 text-sm shadow-xl" style={{ top: open.top, right: open.right }}>
                <div className="px-2 pb-1 text-xs text-muted">Colunas</div>
                {choosable.map((h) => (
                  <label key={h.label} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-panel-2">
                    <input type="checkbox" checked={!isHidden(h)} onChange={() => toggle(h)} />
                    <span className="truncate">{h.label}</span>
                    {prefs.widths[h.label] ? <span className="ml-auto text-[11px] text-muted">{prefs.widths[h.label]} px</span> : null}
                  </label>
                ))}
                <div className="mt-1 border-t border-line px-2 pt-2 text-xs text-muted">Arraste a borda de um título para mudar a largura.</div>
                {changed ? <button type="button" className="mt-1 w-full rounded-md px-2 py-1 text-left text-xs text-accent hover:bg-panel-2" onClick={() => { setPrefs({ hidden: [], shown: [], widths: {} }); setOpen(null); }}>Restaurar padrão</button> : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      <style>{css}</style>
      <table ref={table} className={className} {...rest}>{children}</table>
      {edges.map((e) => (
        <div key={e.label} role="separator" aria-orientation="vertical" aria-label={`Largura da coluna ${e.label}`} title="Arraste para mudar a largura; duplo clique volta ao automático"
          className="no-print absolute top-0 z-[2] w-2 -translate-x-1 cursor-col-resize touch-none hover:bg-accent/30"
          style={{ left: e.x, height: e.h }} onPointerDown={(ev) => startDrag(e.label, ev)} onDoubleClick={() => resetWidth(e.label)} />
      ))}
    </div>
  );
}
